import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Divider,
} from '@chakra-ui/react';

const PlayByPlay = ({ plays, currentInning, homeColor, awayColor }) => {
  // Group plays by inning
  const groupedPlays = plays.reduce((acc, play) => {
    const { inning } = play;
    if (!acc[inning]) acc[inning] = [];
    acc[inning].push(play);
    return acc;
  }, {});

  // Convert to sorted array for rendering
  const sortedInnings = Object.keys(groupedPlays).sort((a, b) => b - a); // Newest first

  return (
    <Box w="full" mt={2}>
      <Text fontSize="8px" fontWeight="900" color="hero.subtext" mb={2} letterSpacing="widest">LIVE PLAY-BY-PLAY</Text>
      <Box maxH="300px" overflowY="auto" pr={1} sx={{
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-track': {
          width: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'card.border',
          borderRadius: '24px',
        },
      }}>
        <Accordion allowToggle defaultIndex={[0]}>
          {sortedInnings.map((inning) => {
            const inningPlays = [...groupedPlays[inning]].reverse();
            const isCurrentInning = Number(inning) === Number(currentInning);
            
            return (
              <AccordionItem key={inning} border="none" mb={2}>
                <h2>
                  <AccordionButton 
                    bg={isCurrentInning ? "brand.navy" : "pbp.header.bg"} 
                    color={isCurrentInning ? "white" : "pbp.header.text"}
                    _hover={{ bg: isCurrentInning ? "brand.blue" : "pbp.item.hover" }}
                    p={3}
                  >
                    <Box flex="1" textAlign="left" fontWeight="900" fontSize="xs">
                      INNING {inning}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={3} px={0} bg="pbp.panel.bg" border="1px solid" borderColor="card.border">
                  <VStack align="stretch" spacing={0} divider={<Divider borderColor="card.border" />}>
                    {inningPlays.map((play, idx) => {
                      const isAdvisory = play.result === 'Game Advisory';
                      
                      if (isAdvisory) {
                        return (
                          <Box key={idx} p={3} bg="pbp.advisory.bg" borderLeft="4px solid" borderColor="brand.blue">
                            <HStack spacing={3}>
                              <Badge variant="solid" bg="brand.blue" color="white" fontSize="9px">INFO</Badge>
                              <Text fontSize="xs" fontWeight="bold" color="hero.subtext" fontStyle="italic">
                                {play.result} — Start pending
                              </Text>
                            </HStack>
                          </Box>
                        );
                      }

                      return (
                        <Box 
                          key={idx} 
                          p={3} 
                          bg={play.halfInning === 'top' ? `${awayColor}1a` : `${homeColor}1a`} // 10% opacity (0.1)
                          borderLeft="4px solid"
                          borderColor={play.halfInning === 'top' ? awayColor : homeColor}
                          _hover={{ bg: play.halfInning === 'top' ? `${awayColor}33` : `${homeColor}33` }} // 20% opacity
                        >
                          <HStack justify="space-between" align="start">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="900" color="pbp.panel.text">{play.batterName}</Text>
                              <Text fontSize="10px" color="hero.subtext" fontWeight="bold">{play.result}</Text>
                            </VStack>
                            <VStack align="end" spacing={1}>
                              <Badge 
                                variant="solid" 
                                bg={play.usedBalls.length > 0 ? "bag.fill" : "pbp.header.bg"}
                                color={play.usedBalls.length > 0 ? "white" : "hero.subtext"}
                                fontSize="9px"
                                borderRadius="full" // Round for better broadcast aesthetic
                                px={2}
                                border="1px solid"
                                borderColor="whiteAlpha.400"
                                boxShadow="sm"
                              >
                                {play.usedBalls.length} B
                              </Badge>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Box>
    </Box>
  );
};

export default PlayByPlay;
